import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import InstanceMCRow from './components/instanceMCRow';
import { goalActions } from '../../../../../../../../../store';
import { MasteryCriteriaChangeTypeEnum } from '../../../../constants/general';

import type { TreatmentPlanResourceVersion } from '../../../../constants/general';
import type {
  ISAGInstanceMasteryCriteriaItem,
  ITreatmentPlanGoal,
} from '../../../../../../../../../utils/types';
import type { ISAGInstanceMastryCriteriaState } from '../common/types/states';

interface ISAGInstanceMasteryCriteriaV2Props {
  goal: ITreatmentPlanGoal;
  goalIndex: number;
  isEditable: boolean;
  goalVersion: TreatmentPlanResourceVersion;
}

function isSAGInstanceMasteryCriteria(
  criteria: ITreatmentPlanGoal['mastery_criteria'],
): criteria is ISAGInstanceMasteryCriteriaItem {
  return Boolean(
    criteria &&
      'acquisition' in criteria &&
      'maintenance_frequency' in criteria &&
      'practice_duration_unit' in criteria.acquisition,
  );
}

function SAGInstanceMasteryCriteriaV2({
  goal,
  goalIndex,
  goalVersion,
  isEditable,
}: ISAGInstanceMasteryCriteriaV2Props) {
  const dispatch = useDispatch();

  const masteryCriteria = isSAGInstanceMasteryCriteria(goal.mastery_criteria)
    ? goal.mastery_criteria
    : null;
  const { acquisition, generalization, maintenance, maintenance_frequency } = masteryCriteria ?? {};
  const baselineAtCreation =
    goal.baseline_at_creation === null ? null : Number(goal.baseline_at_creation);

  const [isAcquisitionLoaded, setIsAcquisitionLoaded] = useState(Boolean(acquisition));

  const [masteryCriteriaState, setMasteryCriteriaMCState] =
    useState<ISAGInstanceMastryCriteriaState>({
      acquisition: {
        change_type: acquisition?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: acquisition?.desired_amount ?? null,
        practice_duration_count: acquisition?.practice_duration_count ?? null,
        practice_duration_unit: null,
      },
      generalization: {
        change_type: generalization?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: generalization?.desired_amount ?? null,
        practice_duration_count: generalization?.practice_duration_count ?? null,
        practice_duration_unit: null,
      },
      maintenance: {
        change_type: maintenance?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: maintenance?.desired_amount ?? null,
        practice_duration_count: maintenance?.practice_duration_count ?? null,
        practice_duration_unit: null,
      },
      maintenance_frequency: maintenance_frequency ?? null,
    });
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (acquisition && !isAcquisitionLoaded) {
      setIsAcquisitionLoaded(true);
    }
  }, [acquisition]);

  useEffect(() => {
    const masteryCriteria = isSAGInstanceMasteryCriteria(goal.mastery_criteria)
      ? goal.mastery_criteria
      : null;
    const { acquisition, generalization, maintenance, maintenance_frequency } =
      masteryCriteria ?? {};
    if (!goal?.is_editing) {
      setIsDirty(false);
    }
    setMasteryCriteriaMCState({
      acquisition: {
        change_type: acquisition?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: acquisition?.desired_amount ?? null,
        practice_duration_count: acquisition?.practice_duration_count ?? null,
        practice_duration_unit: acquisition?.practice_duration_unit ?? null,
      },
      generalization: {
        change_type: generalization?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: generalization?.desired_amount ?? null,
        practice_duration_count: generalization?.practice_duration_count ?? null,
        practice_duration_unit: generalization?.practice_duration_unit ?? null,
      },
      maintenance: {
        change_type: maintenance?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: maintenance?.desired_amount ?? null,
        practice_duration_count: maintenance?.practice_duration_count ?? null,
        practice_duration_unit: maintenance?.practice_duration_unit ?? null,
      },
      maintenance_frequency: maintenance_frequency ?? null,
    });
  }, [goal?.is_editing, isAcquisitionLoaded, goal?.id]);

  const handleAcquisitionMCState = (key: string, value: number | string | null) => {
    setMasteryCriteriaMCState((prev) => ({
      ...prev,
      acquisition: {
        ...prev.acquisition,
        [key]: value,
      },
    }));
    setIsDirty(true);
  };

  const handleGeneralizationMCState = (key: string, value: number | string | null) => {
    setMasteryCriteriaMCState((prev) => ({
      ...prev,
      generalization: {
        ...prev.generalization,
        [key]: value,
      },
    }));
    setIsDirty(true);
  };

  const handleMaintenanceMCState = (key: string, value: number | string | null) => {
    setMasteryCriteriaMCState((prev) => ({
      ...prev,
      maintenance: {
        ...prev.maintenance,
        [key]: value,
      },
    }));
    setIsDirty(true);
  };

  useEffect(() => {
    if (!isDirty) return;
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'mastery_criteria',
        value: masteryCriteriaState,
      }),
    );
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'dirty',
        value: true,
      }),
    );
  }, [masteryCriteriaState]);

  return (
    <>
      <InstanceMCRow
        onUpdate={handleAcquisitionMCState}
        goalIndex={goalIndex}
        title="Acquisition"
        isEditing={Boolean(goal?.is_editing)}
        baseline_at_creation={baselineAtCreation}
        values={masteryCriteriaState.acquisition}
        isEditable={isEditable}
        goalVersion={goalVersion}
      />
      <InstanceMCRow
        onUpdate={handleGeneralizationMCState}
        goalIndex={goalIndex}
        title="Generalization"
        isEditing={Boolean(goal?.is_editing)}
        baseline_at_creation={baselineAtCreation}
        values={masteryCriteriaState.generalization}
        isEditable={isEditable}
        goalVersion={goalVersion}
      />
      <InstanceMCRow
        onUpdate={handleMaintenanceMCState}
        goalIndex={goalIndex}
        title="Maintenance"
        isEditing={Boolean(goal?.is_editing)}
        baseline_at_creation={baselineAtCreation}
        values={masteryCriteriaState.maintenance}
        maintenance_frequency={masteryCriteriaState.maintenance_frequency}
        isEditable={isEditable}
        goalVersion={goalVersion}
        showFrequency
        updateMaintenanceFrequency={(val) => {
          setMasteryCriteriaMCState((prev) => ({
            ...prev,
            maintenance_frequency: val,
          }));
          setIsDirty(true);
        }}
      />
    </>
  );
}

export default SAGInstanceMasteryCriteriaV2;
